import { Form, Formik } from 'formik';
import { TextField } from '../Form';
import SelectCategory from './SelectCategory';
import TextArea from '../Form/TextArea';
import Dropzone from '../Form/Dropzone';
import Modal from '../Modal';
import { useTranslation } from 'react-i18next';
import React, { ReactNode, useContext, useState } from 'react';
import { SprawneMiastoContext } from '../SprawneMiastoContext';
import { ReportService } from '../../utils/reportService';
import {
    MdOutlinedFlag,
    MdOutlineEditNote,
    MdOutlineEmail,
    MdOutlineImage,
    MdSend,
    MdWarningAmber,
} from 'react-icons/md';
import * as Yup from 'yup';
import { ISimilarReport } from '../../models';
import ReportedModal from './ReportedModal';
import SimilarReport from './SimilarReport';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export default function ReportForm(props: {
    show: boolean;
    onClose: () => void;
    coordinates: google.maps.LatLng;
}) {
    const { t } = useTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [similarReport, setSimilarReport] = useState<ISimilarReport | null>(null);

    const [reportId, setReportId] = useState<string | undefined>(undefined);

    const Context = useContext(SprawneMiastoContext);

    const Report = new ReportService({
        localization: {
            lat: props.coordinates.toJSON().lat,
            lon: props.coordinates.toJSON().lng,
        },
    });

    async function FindSimilarReports(category: string) {
        const report = await Report.findSimilarReport({ category: Number(category) });
        setSimilarReport(report);
    }

    return (
        <>
            <Modal show={props.show} onClose={props.onClose} width={1000} showClose={true}>
                <div className={'my-1.5 rounded border-2 border-SM-yellow px-5 py-10'}>
                    <h1 className={'mb-6 mb-12 text-center text-4xl font-medium sm:text-6xl'}>
                        {t('translation:REPORT.NEW_REPORT.REPORTING')}
                    </h1>

                    <Formik
                        initialValues={{
                            title: '',
                            description: '',
                            category: '',
                            email: '',
                            files: [],
                        }}
                        validationSchema={Yup.object().shape({
                            title: Yup.string()
                                .min(5, `${t('translation:ERRORS.WEB_VALIDATION.5')} 5`)
                                .max(256, `${t('translation:ERRORS.WEB_VALIDATION.5')} 256`)
                                .required(t('translation:ERRORS.WEB_VALIDATION.0') as string),
                            description: Yup.string()
                                .min(10, `${t('translation:ERRORS.WEB_VALIDATION.5')} 10`)
                                .max(512, `${t('translation:ERRORS.WEB_VALIDATION.5')} 512`)
                                .required(t('translation:ERRORS.WEB_VALIDATION.0') as string),
                            category: Yup.number().required(
                                t('translation:ERRORS.WEB_VALIDATION.0') as string,
                            ),
                            email: Yup.string()
                                .email(t('translation:ERRORS.WEB_VALIDATION.4') as string)
                                .test(
                                    'check-for-authorization',
                                    t('translation:ERRORS.WEB_VALIDATION.0') as string,
                                    (value) => {
                                        if (!value) {
                                            return !!Context?.user;
                                        }
                                        return true;
                                    },
                                ),
                        })}
                        onSubmit={async () => {
                            setReportId("1");
                            props.onClose();
                        }}>
                        {(fProps) => (
                            <Form
                                className={
                                    ' ml-auto mr-auto flex w-full w-full flex-col items-center gap-10 md:w-2/3'
                                }>
                                <SectionBox
                                    icon={
                                        <MdWarningAmber
                                            className={'ml-1 text-4xl text-SM-yellow'}
                                        />
                                    }
                                    number={'01'}
                                    title={t('translation:REPORT.NEW_REPORT.SECTIONS.1.TITLE')}
                                    description={t(
                                        'translation:REPORT.NEW_REPORT.SECTIONS.1.CONTENT',
                                    )}>
                                    <TextField
                                        placeholder={
                                            t('translation:REPORT.NEW_REPORT.TITLE') as string
                                        }
                                        name={'title'}
                                        type={'text'}
                                        formik={fProps}
                                    />
                                </SectionBox>

                                <SectionBox
                                    icon={
                                        <MdOutlinedFlag
                                            className={'ml-1 text-4xl text-SM-yellow'}
                                        />
                                    }
                                    number={'02'}
                                    title={t('translation:REPORT.NEW_REPORT.SECTIONS.2.TITLE')}
                                    description={t(
                                        'translation:REPORT.NEW_REPORT.SECTIONS.2.CONTENT',
                                    )}>
                                    <SelectCategory formik={fProps} onChange={FindSimilarReports} />
                                </SectionBox>

                                <SectionBox
                                    icon={
                                        <MdOutlineEditNote
                                            className={'ml-1 text-4xl text-SM-yellow'}
                                        />
                                    }
                                    number={'03'}
                                    title={t('translation:REPORT.NEW_REPORT.SECTIONS.3.TITLE')}
                                    description={t(
                                        'translation:REPORT.NEW_REPORT.SECTIONS.3.CONTENT',
                                    )}>
                                    <TextArea
                                        rows={3}
                                        labelClass={'ml-auto mr-auto text-gray-500 mb-3'}
                                        name={'description'}
                                        formik={fProps}
                                        className={'flex w-full flex-col'}
                                    />
                                </SectionBox>

                                {Context?.user ? (
                                    <SectionBox
                                        icon={
                                            <MdOutlineImage
                                                className={'ml-1 text-4xl text-SM-yellow'}
                                            />
                                        }
                                        number={'04'}
                                        title={t(
                                            'translation:REPORT.NEW_REPORT.SECTIONS.4.UPLOAD_TITLE',
                                        )}
                                        description={t(
                                            'translation:REPORT.NEW_REPORT.SECTIONS.4.UPLOAD_CONTENT',
                                        )}>
                                        <Dropzone
                                            name={'files'}
                                            formik={fProps}
                                            formats={'JPG, PNG'}
                                            dropZoneOptions={{
                                                maxFiles: 3,
                                                accept: {
                                                    'image/jpeg': [],
                                                    'image/png': [],
                                                },
                                            }}
                                        />
                                    </SectionBox>
                                ) : (
                                    <SectionBox
                                        icon={
                                            <MdOutlineEmail
                                                className={'ml-1 text-4xl text-SM-yellow'}
                                            />
                                        }
                                        number={'04'}
                                        title={t(
                                            'translation:REPORT.NEW_REPORT.SECTIONS.4.EMAIL_TITLE',
                                        )}
                                        description={t(
                                            'translation:REPORT.NEW_REPORT.SECTIONS.4.EMAIL_CONTENT',
                                        )}>
                                        <TextField
                                            placeholder={
                                                t('translation:REPORT.NEW_REPORT.EMAIL') as string
                                            }
                                            name={'email'}
                                            type={'email'}
                                            formik={fProps}
                                        />
                                    </SectionBox>
                                )}
                                <div className={'flex flex-col items-center '}>
                                    <button
                                        type="submit"
                                        className={
                                            'btn-filled bg-SM-yellow py-2 px-3 text-white ring-SM-yellow/50'
                                        }>
                                        <MdSend className={'text-2xl'} />
                                        {t('translation:REPORT.NEW_REPORT.SEND_REPORT')}
                                    </button>
                                    <p className={'mt-3 text-center text-xs text-red-500'}>
                                        {t(
                                            'translation:REPORT.NEW_REPORT.EMERGENCY_CASE_DISCLAIMER',
                                        )}
                                    </p>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Modal>

            <ReportedModal reportId={reportId} onClose={() => setReportId(undefined)} />
            <SimilarReport onClose={() => setSimilarReport(null)} report={similarReport} />
        </>
    );
}

function SectionBox(props: {
    children: ReactNode;
    number: string;
    title: string;
    description: string;
    icon: ReactNode;
}) {
    return (
        <div className={'w-full rounded border-2 border-SM-yellow'}>
            <span
                className={
                    'absolute -translate-y-7 translate-x-2 bg-white px-1 text-5xl font-bold font-black text-SM-yellow'
                }>
                {props.number}
            </span>
            <div className={'inline-block flex flex-col p-5'}>
                {props.icon}
                <span className={'ml-1 font-medium'}>{props.title}</span>
                <span className={'ml-1 mb-2 text-xs'}>{props.description}</span>
                {props.children}
            </div>
        </div>
    );
}
